import { useCallback, useRef, useState } from 'react';
import { durationMinutes } from '@fsm/shared';
import type { BoardColumn, BoardJob, DropTarget } from './types';
import { BOARD, offsetToTime, timeToOffset } from './constants';

/**
 * Pointer-driven drag for the dispatch board.
 *
 * Deliberately not HTML5 drag-and-drop: that API cannot show a live snapped
 * preview, behaves differently in every browser, and does not work with touch.
 * Pointer events give the same code path for a mouse, a trackpad and a
 * dispatcher poking at a touchscreen.
 *
 * A drag is only a drag once the pointer has moved a few pixels, so clicking a
 * block to open it still works.
 */

const DRAG_THRESHOLD_PX = 4;

export interface DragState {
  jobId: string;
  /** Live target, recomputed on every move. Null while below the threshold. */
  target: DropTarget | null;
  /** Where the ghost should be drawn, in board coordinates. */
  ghostTop: number;
  ghostColumnKey: string;
  height: number;
}

export interface UseBoardDragArgs {
  columns: BoardColumn[];
  onDrop: (job: BoardJob, target: DropTarget) => void;
  /** Called on a click that never became a drag. */
  onClick?: (job: BoardJob) => void;
  /** Live conflict lookup so the ghost can turn red before the drop. */
  hasConflict?: (job: BoardJob, target: DropTarget) => boolean;
}

export function useBoardDrag({ columns, onDrop, onClick, hasConflict }: UseBoardDragArgs) {
  const boardRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<DragState | null>(null);

  const session = useRef<{
    job: BoardJob;
    pointerId: number;
    startX: number;
    startY: number;
    /** Where inside the block the pointer grabbed it. */
    grabOffsetY: number;
    durationMin: number;
    moved: boolean;
  } | null>(null);

  /** Which column does this client X fall in? */
  const columnAt = useCallback(
    (clientX: number): BoardColumn | null => {
      const board = boardRef.current;
      if (!board) return null;
      const lanes = board.querySelectorAll<HTMLElement>('[data-column-key]');
      for (const lane of lanes) {
        const rect = lane.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right) {
          const key = lane.dataset['columnKey'];
          return columns.find((c) => c.key === key) ?? null;
        }
      }
      return null;
    },
    [columns],
  );

  const compute = useCallback(
    (clientX: number, clientY: number): DropTarget | null => {
      const board = boardRef.current;
      const current = session.current;
      if (!board || !current) return null;

      // No column under the pointer means no valid drop — the rail, the
      // gutter, or off the edge. Returning null hides the ghost and makes
      // pointerup a no-op, which is what "dragged nowhere" should do.
      const column = columnAt(clientX);
      if (!column) return null;

      const lane = board.querySelector<HTMLElement>(`[data-column-key="${cssEscape(column.key)}"]`);
      if (!lane) return null;

      const rect = lane.getBoundingClientRect();
      const offsetY = clientY - rect.top - current.grabOffsetY;
      const start = offsetToTime(column.day, offsetY);
      const end = new Date(start.getTime() + current.durationMin * 60_000);
      return { column, start, end };
    },
    [columnAt, columns],
  );

  const onPointerDown = useCallback(
    (event: React.PointerEvent, job: BoardJob) => {
      // Left button only; let right-click open a context menu.
      if (event.button !== 0) return;
      const block = event.currentTarget as HTMLElement;
      const rect = block.getBoundingClientRect();

      session.current = {
        job,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        grabOffsetY: event.clientY - rect.top,
        durationMin: durationMinutes(job.start, job.end),
        moved: false,
      };
      block.setPointerCapture(event.pointerId);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      const current = session.current;
      if (!current || event.pointerId !== current.pointerId) return;

      const dx = Math.abs(event.clientX - current.startX);
      const dy = Math.abs(event.clientY - current.startY);
      if (!current.moved && dx < DRAG_THRESHOLD_PX && dy < DRAG_THRESHOLD_PX) return;
      current.moved = true;

      const target = compute(event.clientX, event.clientY);
      if (!target) return;

      setDrag({
        jobId: current.job.id,
        target,
        ghostTop: timeToOffset(target.start),
        ghostColumnKey: target.column.key,
        height: (current.durationMin / 60) * 56,
      });
    },
    [compute],
  );

  const finish = useCallback(
    (event: React.PointerEvent) => {
      const current = session.current;
      if (!current || event.pointerId !== current.pointerId) return;
      (event.currentTarget as HTMLElement).releasePointerCapture?.(event.pointerId);

      if (!current.moved) {
        onClick?.(current.job);
      } else {
        const target = compute(event.clientX, event.clientY);
        if (target && changed(current.job, target)) onDrop(current.job, target);
      }

      session.current = null;
      setDrag(null);
    },
    [compute, onClick, onDrop],
  );

  const cancel = useCallback(() => {
    session.current = null;
    setDrag(null);
  }, []);

  const dragConflicts =
    drag?.target && session.current && hasConflict
      ? hasConflict(session.current.job, drag.target)
      : false;

  return {
    boardRef,
    drag,
    dragConflicts,
    snapMinutes: BOARD.snapMinutes,
    handlers: { onPointerDown, onPointerMove, onPointerUp: finish, onPointerCancel: cancel },
  };
}

function changed(job: BoardJob, target: DropTarget): boolean {
  const techChanged =
    target.column.techId !== null && target.column.techId !== job.assignedTechId;
  return techChanged || target.start.getTime() !== job.start.getTime();
}

/** CSS.escape is not in older Safari; the keys we generate are simple anyway. */
function cssEscape(value: string): string {
  return value.replace(/["\\]/g, '\\$&');
}
